/**
 * Connexion à Supabase Auth — la **première** des deux étapes.
 *
 * `@supabase/auth-js` est volontairement seul : contrairement au paquet complet
 * `@supabase/supabase-js`, il ne sait pas parler à la base. Le code de
 * l'interface est donc dans l'incapacité matérielle de contourner l'API, ce qui
 * est une barrière de structure de plus (cadrage v2, §4).
 *
 * Ce fichier ne décide de rien sur l'accès : il obtient un jeton, ou dit
 * pourquoi il n'en obtient pas. Savoir si la personne a le droit d'entrer est
 * l'affaire de l'API — voir api.ts.
 */

import { AuthClient, isAuthApiError, type Session } from '@supabase/auth-js';
import { configuration } from './configuration.js';

export const authentification = new AuthClient({
  url: `${configuration.supabaseUrl}/auth/v1`,
  headers: { apikey: configuration.supabaseClePublique },
  // Le jeton vaut une heure ; auth-js le renouvelle seul tant que l'onglet vit.
  autoRefreshToken: true,
  persistSession: true,
  // Aucune connexion par lien : rien à détecter dans l'URL.
  detectSessionInUrl: false,
});

/** Ce que la connexion peut refuser, du côté de Supabase. */
export type EchecConnexion =
  'identifiants' | 'non_confirme' | 'trop_de_tentatives' | 'injoignable';

export type ResultatConnexion =
  | { readonly etat: 'ok'; readonly session: Session }
  | { readonly etat: 'echec'; readonly cause: EchecConnexion };

/**
 * Traduit l'erreur de Supabase en une cause que l'écran sait afficher.
 *
 * On s'appuie sur `error.code`, valeur stable documentée, et **jamais** sur le
 * texte du message. Le repli sur `status` couvre les erreurs plus anciennes,
 * qui n'ont pas toujours de code.
 */
export function causeDeLechec(erreur: unknown): EchecConnexion {
  if (!isAuthApiError(erreur)) {
    // Ni réponse, ni code : le serveur n'a pas été joint.
    return 'injoignable';
  }
  switch (erreur.code) {
    case 'invalid_credentials':
      return 'identifiants';
    case 'email_not_confirmed':
      return 'non_confirme';
    case 'over_request_rate_limit':
    case 'over_email_send_rate_limit':
      return 'trop_de_tentatives';
    default:
      break;
  }
  if (erreur.status === 429) {
    return 'trop_de_tentatives';
  }
  if (erreur.status === 400 || erreur.status === 401) {
    return 'identifiants';
  }
  return 'injoignable';
}

export async function seConnecter(
  email: string,
  motDePasse: string,
): Promise<ResultatConnexion> {
  try {
    const { data, error } = await authentification.signInWithPassword({
      email,
      password: motDePasse,
    });
    if (error !== null) {
      return { etat: 'echec', cause: causeDeLechec(error) };
    }
    if (data.session === null) {
      // Ne devrait pas arriver avec un mot de passe, mais un `null` silencieux
      // vaut mieux traité que supposé impossible.
      return { etat: 'echec', cause: 'identifiants' };
    }
    return { etat: 'ok', session: data.session };
  } catch (erreur) {
    return { etat: 'echec', cause: causeDeLechec(erreur) };
  }
}

export async function seDeconnecter(
  /**
   * LOT 170 : « local » quand l'API a coupé la session (24 h, autre poste).
   * La sortie par défaut de Supabase est « globale » : elle fermerait AUSSI
   * le poste qui vient de prendre la main.
   */
  portee: 'global' | 'local' = 'global',
): Promise<void> {
  await authentification.signOut({ scope: portee });
}

/**
 * Le jeton d'accès du moment, ou `null` s'il n'y a plus de séance.
 *
 * À appeler **avant chaque appel à l'API**, jamais à garder dans une variable :
 * le jeton vaut une heure, et `getSession` le renouvelle de lui-même quand il
 * approche de son terme. Un jeton conservé au moment de la connexion aurait
 * expiré au bout d'une heure de travail, et l'écran se serait mis à répondre
 * « accès fermé » sans raison visible.
 */
export async function jetonCourant(): Promise<string | null> {
  const { data } = await authentification.getSession();
  return data.session?.access_token ?? null;
}
